/**
 * The parts of Milk sales and To do (16 October 2026; before that one tickets page named after a buyer, which Ronald
 * called chaos, and MV-3 says no screen is specific to one buyer): what the morning job could not record by
 * itself, the loads recorded lately, and the sales contracts (design point 4 of docs/superpowers/plans/2026-10-04-county-sales-automation.md,
 * agreed with Ronald on 4 October 2026).
 *
 * J.H. Willis mails the Optiload ticket of every load it sells to the milk office. The job reads it and
 * records the load unattended when every check passes; otherwise the load is held here, with the
 * reasons in plain words and a link to the mail with the scan. The milk office corrects the farm, the
 * litres and the time of each line, maps a farm code the planner does not know yet, and confirms the
 * load or rejects it. These litres pay the farmers and invoice the buyer, so nothing is guessed: what the
 * job proposed is shown as proposed, and only a person's confirmation records it. Ian records a
 * contract when the buyer confirms one.
 *
 * A decision keeps its reason (decision C2, Ronald, 5 October 2026; bsd-knowledge v0.80): a rejection
 * must say why, since it leaves a farmer unpaid for that day until it is resolved, and a confirmation
 * may. The reasons are shown with the loads and the rejections, and each contract with who recorded it.
 *
 * A contract is checked against its filed document once, by a person (the confirm step of
 * docs/superpowers/specs/2026-10-14-sales-contracts-filing-design.md, bsd-knowledge v0.118): the table
 * says who and when, or offers "Mark as checked".
 *
 * Above the contracts, the sales offers (spec docs/superpowers/specs/2026-10-14-sales-offers-before-contract-design.md,
 * OF-7 and OF-8): what we offer to sell before the buyer's confirmation, in `SalesOffers.tsx`. Milk trading
 * records them; a person with only that role sees the tickets and contracts say in one sentence that they
 * need the payments role.
 *
 * Every time is typed and shown in the planner's zone, which the held list sends as `time_zone`.
 * Every field is a design system control, the lines are fieldsets a keyboard can reach in order, and
 * the tables scroll inside their own regions on a narrow screen.
 */
import { Badge, Button, Checkbox, Select, SelectItem, TextArea, TextField, Table } from "@depicomnlbv/platform-ui";
import { memo, useCallback, useId, useRef, useState, type FormEvent, type RefObject } from "react";

import {
  PlannerApiError,
  useBuyers,
  useCheckContract,
  useConfirmImport,
  useContracts,
  useHeldImports,
  useLinkLoad,
  useProducers,
  useRecordContract,
  useRejectImport,
  useRejectedImports,
  useSoldLoads,
  useWaitingLoads,
  type Contract,
  type HeldImport,
  type Producer,
  type RejectedImport,
  type SoldLoad,
  type WaitingLoad,
} from "../api";
import { formatMoment } from "../closureForm";
import { Pending } from "../components/Failure";
import {
  buildConfirmation,
  draftsFrom,
  emptyDraft,
  formatPrice,
  parseLitres,
  parsePrice,
  plainProblem,
  totalLitres,
  type Errors,
  type LineDraft,
} from "../salesForm";
import { formatLitres, formatShortDate, formatTime, isCalendarDate } from "../time";

import { detailOf, focusFirstInvalid, ScreenRefused } from "./salesRefusal";

import "./milkSales.css";

/** One farm line of a held load. Memoised, with callbacks that name the line by its key, so a keystroke in one
 *  line re-renders that line alone and not every line's farm list (each a Select over all producers). */
const FarmLine = memo(function FarmLine({
  draft,
  index,
  producers,
  errors,
  onChange,
  onRemove,
  timeZone,
}: {
  draft: LineDraft;
  index: number;
  producers: Producer[];
  errors: Errors;
  /** Takes the line's new draft; the draft's key says which line it is. */
  onChange: (draft: LineDraft) => void;
  onRemove: (key: string) => void;
  timeZone: string;
}) {
  const n = index + 1;
  const error = (field: string) => errors[`${draft.key}.${field}`];
  const read = draft.farmAsRead ? `read as “${draft.farmAsRead}”` : "added by hand";
  const code = draft.locationCode ? `, code ${draft.locationCode}` : "";
  return (
    <fieldset className="milk-sales-line">
      <legend>
        Line {n}: {read}
        {code}
      </legend>
      {draft.locationCode && !draft.codeKnown ? (
        <p className="milk-sales__hint">The planner does not know code {draft.locationCode} yet.</p>
      ) : null}
      <div className="milk-sales-line__fields">
        <Select
          errorMessage={error("farm")}
          isInvalid={Boolean(error("farm"))}
          label="Farm"
          onSelectionChange={(key) => onChange({ ...draft, producerId: key === null ? null : Number(key) })}
          placeholder="Choose the farm"
          selectedKey={draft.producerId === null ? null : String(draft.producerId)}
        >
          {producers.map((p) => (
            <SelectItem id={String(p.producer_id)} key={p.producer_id} textValue={p.farm_name}>
              {p.farm_name}
              {p.producer_number !== null ? ` (${p.producer_number})` : ""}
            </SelectItem>
          ))}
        </Select>
        <TextField
          description={`In ${timeZone} time.`}
          errorMessage={error("collectedAt")}
          isInvalid={Boolean(error("collectedAt"))}
          label="Collected at"
          onChange={(value) => onChange({ ...draft, collectedAt: value })}
          type="datetime-local"
          value={draft.collectedAt}
        />
        <TextField
          errorMessage={error("litres")}
          inputMode="decimal"
          isInvalid={Boolean(error("litres"))}
          label="Litres"
          onChange={(value) => onChange({ ...draft, litres: value })}
          value={draft.litres}
        />
        <TextField
          errorMessage={error("ticketNumber")}
          isInvalid={Boolean(error("ticketNumber"))}
          label="Ticket"
          onChange={(value) => onChange({ ...draft, ticketNumber: value })}
          value={draft.ticketNumber}
        />
      </div>
      <div className="milk-sales__actions">
        {draft.locationCode && !draft.codeKnown ? (
          <Checkbox isSelected={draft.remember} onChange={(remember) => onChange({ ...draft, remember })}>
            Remember code {draft.locationCode} as this farm
          </Checkbox>
        ) : null}
        <Button onPress={() => onRemove(draft.key)} variant="subtle">
          Remove line {n}
        </Button>
      </div>
    </fieldset>
  );
});

function HeldCard({
  imp,
  timeZone,
  producers,
  onDone,
}: {
  imp: HeldImport;
  timeZone: string;
  producers: Producer[];
  /** Said once the decision is saved; the card leaves the list then. */
  onDone: (message: string) => void;
}) {
  const confirm = useConfirmImport();
  const reject = useRejectImport();
  const [drafts, setDrafts] = useState<LineDraft[]>(() => draftsFrom(imp, timeZone));
  const [loadDate, setLoadDate] = useState(imp.proposed.load_date ?? "");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const headingId = useId();

  const title = imp.mail?.subject || imp.attachment_name;
  const total = totalLitres(drafts);
  const printed = imp.summaries.reduce((sum, s) => sum + (s.milk_collected ?? 0), 0);
  const transferred = imp.summaries.flatMap((s) => s.transfers).reduce((sum, t) => sum + (t.litres ?? 0), 0);

  const refuse = (found: Errors) => {
    setErrors(found);
    const n = Object.keys(found).length;
    setSummary(`The load was not confirmed: ${n} ${n === 1 ? "field needs" : "fields need"} attention.`);
    focusFirstInvalid(form.current);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Enter pressed twice would send the confirmation twice.
    if (confirm.isPending) return;
    setSummary(null);
    setRefused(null);
    const built = buildConfirmation(drafts, loadDate, note, timeZone);
    if ("errors" in built) {
      refuse(built.errors);
      return;
    }
    setErrors({});
    confirm.mutate(
      { importId: imp.import_id, body: built.body },
      {
        onSuccess: (done) => {
          const remembered =
            done.codes_remembered.length > 0 ? ` Code ${done.codes_remembered.join(", ")} remembered.` : "";
          onDone(`Confirmed: ${formatLitres(done.litres)} from “${title}”.${remembered}`);
        },
        onError: (error) => setRefused(detailOf(error)),
      },
    );
  };

  const sendReject = (event: FormEvent) => {
    event.preventDefault();
    if (reject.isPending) return;
    setRefused(null);
    if (!reason.trim()) {
      setReasonError("Say why this is not a load to record");
      focusFirstInvalid(form.current);
      return;
    }
    setReasonError(null);
    reject.mutate(
      { importId: imp.import_id, reason: reason.trim() },
      {
        onSuccess: () => onDone(`Rejected: “${title}”. Nothing was recorded from it.`),
        onError: (error) => setRefused(detailOf(error)),
      },
    );
  };

  // Stable across renders, so the memoised FarmLine of a line nobody typed in is skipped.
  const change = useCallback(
    (draft: LineDraft) => setDrafts((all) => all.map((d) => (d.key === draft.key ? draft : d))),
    [],
  );
  const remove = useCallback((key: string) => setDrafts((all) => all.filter((d) => d.key !== key)), []);

  return (
    <article aria-labelledby={headingId} className="panel milk-sales-held">
      <h4 className="milk-sales-held__title" id={headingId}>
        {title}
      </h4>
      <p className="milk-sales__detail">
        Received {formatMoment(imp.received_at, timeZone)}
        {imp.hauler ? ` from ${imp.hauler}` : ""}
        {imp.mail?.sender ? ` (${imp.mail.sender})` : ""}
      </p>
      <h5 className="milk-sales-held__label">Why it waits for you</h5>
      <ul className="milk-sales-held__problems">
        {imp.problems.map((p) => (
          <li key={p}>{plainProblem(p)}</li>
        ))}
      </ul>
      {imp.mail?.web_link ? (
        <p>
          <a href={imp.mail.web_link} rel="noopener noreferrer" target="_blank">
            Open the mail with the scan
            <span className="visually-hidden"> (opens Outlook in a new tab)</span>
          </a>
        </p>
      ) : imp.scan?.web_url ? (
        <p>
          <a href={imp.scan.web_url} rel="noopener noreferrer" target="_blank">
            Open the scan
            <span className="visually-hidden"> (opens SharePoint in a new tab)</span>
          </a>
        </p>
      ) : imp.scan ? (
        <p className="milk-sales__detail">
          The scan's link was not kept; find {imp.scan.name ?? imp.attachment_name} in Milk Intake › Optiload Milk
          Tickets on the MilkSupply site.
        </p>
      ) : (
        <p className="milk-sales__detail">The mail's link was not kept; find it in the milk office's mailbox.</p>
      )}
      <dl className="milk-sales-held__read">
        <div>
          <dt>Ticket total</dt>
          <dd>{printed ? formatLitres(printed) : "not read"}</dd>
        </div>
        <div>
          <dt>Into the buyer's tanker</dt>
          <dd>{transferred ? formatLitres(transferred) : "not read"}</dd>
        </div>
        <div>
          <dt>Sold (transferred)</dt>
          <dd>
            {imp.proposed.delivery_note_litres !== null ? formatLitres(imp.proposed.delivery_note_litres) : "none"}
          </dd>
        </div>
        <div>
          <dt>Farms below</dt>
          <dd>{formatLitres(total)}</dd>
        </div>
      </dl>
      {imp.text ? (
        <details className="milk-sales-held__text">
          <summary>What the scan was read as</summary>
          <pre>{imp.text}</pre>
        </details>
      ) : null}

      <form className="milk-sales-form" noValidate onSubmit={rejecting ? sendReject : submit} ref={form}>
        {summary ? (
          <p className="milk-sales__error" role="alert">
            {summary}
          </p>
        ) : null}
        <TextField
          description={imp.proposed.load_date ? "The day of the transfer into the buyer's tanker." : "Not read: fill it in."}
          errorMessage={errors.loadDate}
          isInvalid={Boolean(errors.loadDate)}
          label="Day of the load"
          onChange={setLoadDate}
          type="date"
          value={loadDate}
        />
        {drafts.map((draft, i) => (
          <FarmLine
            draft={draft}
            errors={errors}
            index={i}
            key={draft.key}
            onChange={change}
            onRemove={remove}
            producers={producers}
            timeZone={timeZone}
          />
        ))}
        {errors.lines ? <p className="milk-sales__error">{errors.lines}</p> : null}
        <div className="milk-sales__actions">
          <Button onPress={() => setDrafts([...drafts, emptyDraft()])} variant="secondary">
            Add a farm line
          </Button>
        </div>
        <TextArea
          description="Optional. Kept with your decision and shown with the load: what was corrected, and why."
          label="Note on the load"
          onChange={setNote}
          rows={2}
          value={note}
        />
        {rejecting ? (
          <div className="milk-sales-form__reject">
            <TextArea
              description="Kept with the ticket and shown under Rejected lately. The farmers on it are not paid for that day until it is resolved."
              errorMessage={reasonError ?? undefined}
              isInvalid={Boolean(reasonError)}
              label="Why is this not a load?"
              onChange={setReason}
              rows={2}
              value={reason}
            />
            <div className="milk-sales__actions">
              <Button isPending={reject.isPending} pendingLabel="Rejecting" type="submit">
                Reject import
              </Button>
              <Button onPress={() => setRejecting(false)} variant="subtle">
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <div className="milk-sales__actions">
            <Button isPending={confirm.isPending} pendingLabel="Confirming" type="submit">
              Confirm load
            </Button>
            <Button
              onPress={() => {
                setRejecting(true);
                setRefused(null);
              }}
              variant="secondary"
            >
              Reject
            </Button>
          </div>
        )}
        {refused ? (
          <p className="milk-sales__error" role="alert">
            {refused}
          </p>
        ) : null}
      </form>
    </article>
  );
}

export function HeldLoads({ onDone }: { onDone: (message: string) => void }) {
  const held = useHeldImports();
  const producers = useProducers();
  if (held.isPending || producers.isPending) return <Pending>Reading the held loads</Pending>;
  if (held.isError) return <ScreenRefused error={held.error} />;
  if (producers.isError) return <ScreenRefused error={producers.error} />;
  const { imports, time_zone: timeZone } = held.data;
  if (imports.length === 0) {
    return <p className="empty">Nothing is waiting: every ticket read so far was recorded or decided.</p>;
  }
  return (
    <>
      {imports.map((imp) => (
        <HeldCard
          imp={imp}
          key={imp.import_id}
          onDone={onDone}
          producers={producers.data.producers}
          timeZone={timeZone}
        />
      ))}
    </>
  );
}

/** One load whose delivery note waits: the milk office names its contract, and gives the buyer's litres when the
 *  load has none. The planner checks the contract (the buyer's, submitted, its period holding the day). */
function WaitingCard({ load, onDone }: { load: WaitingLoad; onDone: (message: string) => void }) {
  const link = useLinkLoad();
  const own = load.contract_number !== null ? (load.contracts[0]?.sales_contract_id ?? null) : null;
  const [contract, setContract] = useState<number | null>(own);
  const [litres, setLitres] = useState("");
  const [errors, setErrors] = useState<{ contract?: string; litres?: string }>({});
  const [refused, setRefused] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const headingId = useId();
  const needsLitres = load.delivery_note_litres === null;
  const title = `${load.buyer}, ${formatShortDate(load.load_date)}`;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (link.isPending) return;
    setRefused(null);
    const given = litres.trim() ? parseLitres(litres) : null;
    const found: { contract?: string; litres?: string } = {};
    if (contract === null) found.contract = "Choose the contract this load was sold on";
    if (litres.trim() && (given === null || given <= 0)) found.litres = "Litres as a number, such as 27,350 or 27350.5";
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(form.current);
      return;
    }
    link.mutate(
      {
        loadId: load.sold_load_id,
        body: { sales_contract_id: contract as number, ...(given !== null ? { delivery_note_litres: given } : {}) },
      },
      {
        onSuccess: (done) =>
          onDone(
            done.complete
              ? `Linked: ${title} on contract ${done.contract_number ?? ""}, ${formatLitres(done.delivery_note_litres ?? 0)} sold.`
              : `Linked: ${title} on contract ${done.contract_number ?? ""}. It waits for the buyer's litres.`,
          ),
        onError: (error) => setRefused(detailOf(error)),
      },
    );
  };

  return (
    <article aria-labelledby={headingId} className="panel milk-sales-held">
      <h4 className="milk-sales-held__title" id={headingId}>
        {title}
      </h4>
      <p className="milk-sales__detail">
        {load.waits_for === "contract"
          ? "No contract was named when the load was recorded: more than one contract holds the day, or none does."
          : `On contract ${load.contract_number}; the buyer's litres (the D600) have not come yet.`}
      </p>
      <dl className="milk-sales-held__read">
        <div>
          <dt>Farms on the load</dt>
          <dd>{formatLitres(load.litres)}</dd>
        </div>
        <div>
          <dt>Sold (transferred)</dt>
          <dd>{load.delivery_note_litres !== null ? formatLitres(load.delivery_note_litres) : "none yet"}</dd>
        </div>
      </dl>
      <form className="milk-sales-form" noValidate onSubmit={submit} ref={form}>
        {load.contracts.length === 0 ? (
          <p className="milk-sales__detail">
            No contract of {load.buyer} holds {formatShortDate(load.load_date)}. Record the contract under Milk trading
            first; it is offered here then.
          </p>
        ) : (
          <Select
            errorMessage={errors.contract}
            isDisabled={own !== null}
            isInvalid={Boolean(errors.contract)}
            label="Contract"
            onSelectionChange={(key) => setContract(key === null ? null : Number(key))}
            placeholder="Choose the contract"
            selectedKey={contract === null ? null : String(contract)}
          >
            {load.contracts.map((c) => (
              <SelectItem id={String(c.sales_contract_id)} key={c.sales_contract_id} textValue={c.contract_number}>
                {c.contract_number} ({formatShortDate(c.period_from)} to {formatShortDate(c.period_to)})
              </SelectItem>
            ))}
          </Select>
        )}
        {needsLitres ? (
          <TextField
            description="The buyer's litres from the D600. Optional: without them the load stays here, linked, until they come."
            errorMessage={errors.litres}
            inputMode="decimal"
            isInvalid={Boolean(errors.litres)}
            label="Sold (transferred), litres"
            onChange={setLitres}
            value={litres}
          />
        ) : null}
        <div className="milk-sales__actions">
          <Button
            isDisabled={load.contracts.length === 0}
            isPending={link.isPending}
            pendingLabel="Linking"
            type="submit"
          >
            {load.waits_for === "contract" ? "Link to contract" : "Record the litres"}
          </Button>
        </div>
        {refused ? (
          <p className="milk-sales__error" role="alert">
            {refused}
          </p>
        ) : null}
      </form>
    </article>
  );
}

export function WaitingLoads({ onDone }: { onDone: (message: string) => void }) {
  const waiting = useWaitingLoads();
  if (waiting.isPending) return <Pending>Reading the loads that wait</Pending>;
  if (waiting.isError) return <ScreenRefused error={waiting.error} />;
  if (waiting.data.loads.length === 0) {
    return <p className="empty">No load waits: every load recorded has its contract and its litres.</p>;
  }
  return (
    <>
      {waiting.data.loads.map((load) => (
        <WaitingCard key={load.sold_load_id} load={load} onDone={onDone} />
      ))}
    </>
  );
}

function RecordedHow({ load, timeZone }: { load: SoldLoad; timeZone: string }) {
  return (
    <>
      {recordedHow(load, timeZone)}
      {load.decision_note ? <p className="milk-sales__detail">Note: {load.decision_note}</p> : null}
    </>
  );
}

function recordedHow(load: SoldLoad, timeZone: string): string {
  if (load.import_status === "accepted") return "Read unattended";
  if (load.import_status === "confirmed") {
    const when = load.decided_at ? ` on ${formatMoment(load.decided_at, timeZone)}` : "";
    return `Confirmed by ${load.decided_by ?? "someone"}${when}`;
  }
  return "Entered by hand";
}

export function RecentLoads({ buyer = null }: { buyer?: string | null }) {
  const loads = useSoldLoads();
  if (loads.isPending) return <Pending>Reading the recent loads</Pending>;
  if (loads.isError) return <ScreenRefused error={loads.error} part="sales" />;
  const { time_zone: timeZone, from, to } = loads.data;
  const rows = loads.data.loads.filter((l) => buyer === null || l.buyer === buyer);
  if (rows.length === 0) {
    return <p className="empty">No load was recorded from {formatShortDate(from)} to {formatShortDate(to)}.</p>;
  }
  return (
    <Table caption={`Loads sold from ${formatShortDate(from)} to ${formatShortDate(to)}, newest first`} className="milk-sales-table">
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col">Farms</th>
            <th scope="col">Collected</th>
            <th scope="col">Sold (transferred)</th>
            <th scope="col">Contract</th>
            <th scope="col">Recorded</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((load) => (
            <tr key={load.sold_load_id}>
              <th scope="row" className="tabular">
                {formatShortDate(load.load_date)}
              </th>
              <td>
                <ul className="milk-sales-table__lines">
                  {load.lines.map((ln) => (
                    <li key={`${ln.producer_id}-${ln.ticket_number ?? ""}`}>
                      {ln.farm_name}: {formatLitres(ln.litres)}
                      {ln.collected_at ? ` at ${formatTime(ln.collected_at, timeZone)}` : ""}
                    </li>
                  ))}
                </ul>
              </td>
              <td className="tabular">{formatLitres(load.litres)}</td>
              <td className="tabular">
                {load.delivery_note_litres !== null ? formatLitres(load.delivery_note_litres) : "None"}
              </td>
              <td>{load.contract_number ?? <Badge tone="high">No contract</Badge>}</td>
              <td>
                <RecordedHow load={load} timeZone={timeZone} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
  );
}

function RejectedRow({ imp, timeZone }: { imp: RejectedImport; timeZone: string }) {
  const title = imp.mail?.subject || imp.attachment_name;
  return (
    <tr>
      <th scope="row">
        {imp.mail?.web_link ? (
          <a href={imp.mail.web_link} rel="noopener noreferrer" target="_blank">
            {title}
            <span className="visually-hidden"> (opens Outlook in a new tab)</span>
          </a>
        ) : (
          title
        )}
        <p className="milk-sales__detail">
          Received {formatMoment(imp.received_at, timeZone)}
          {imp.hauler ? ` from ${imp.hauler}` : ""}
        </p>
      </th>
      <td>{imp.decision_note ?? "No reason was recorded"}</td>
      <td>
        {imp.decided_by ?? "Someone"}
        <p className="milk-sales__detail">{formatMoment(imp.decided_at, timeZone)}</p>
      </td>
    </tr>
  );
}

export function RejectedLately() {
  const rejected = useRejectedImports();
  if (rejected.isPending) return <Pending>Reading the rejected tickets</Pending>;
  if (rejected.isError) return <ScreenRefused error={rejected.error} />;
  const { imports, time_zone: timeZone, from, to } = rejected.data;
  if (imports.length === 0) {
    return <p className="empty">No ticket was rejected from {formatShortDate(from)} to {formatShortDate(to)}.</p>;
  }
  return (
    <Table caption={`Tickets rejected from ${formatShortDate(from)} to ${formatShortDate(to)}, latest first`} className="milk-sales-table">
        <thead>
          <tr>
            <th scope="col">Ticket</th>
            <th scope="col">Why</th>
            <th scope="col">Rejected by</th>
          </tr>
        </thead>
        <tbody>
          {imports.map((imp) => (
            <RejectedRow imp={imp} key={imp.import_id} timeZone={timeZone} />
          ))}
        </tbody>
      </Table>
  );
}

/** Who checked the contract and when, or the button that records the caller as having checked it: for the milk
 *  office only (C1), so a reader or the trader who entered it sees "Not checked" without a button that 403s. */
function Checked({ contract, canCheck }: { contract: Contract; canCheck: boolean }) {
  const check = useCheckContract();
  const [refused, setRefused] = useState<string | null>(null);
  if (contract.checked_at) {
    // The API sends the moment in the planner's zone, so its first ten characters are that zone's day.
    return <>Checked by {contract.checked_by ?? "someone"}, {formatShortDate(contract.checked_at.slice(0, 10))}</>;
  }
  if (!canCheck) return <Badge tone="high">Not checked</Badge>;
  return (
    <>
      <Badge tone="high">Not checked</Badge>
      <div className="milk-sales__actions">
        <Button
          isPending={check.isPending}
          onPress={() => {
            setRefused(null);
            check.mutate(contract.contract_id, { onError: (error) => setRefused(detailOf(error)) });
          }}
          pendingLabel="Marking"
          variant="secondary"
        >
          Mark as checked<span className="visually-hidden">: contract {contract.contract_number}</span>
        </Button>
      </div>
      {refused ? (
        <p className="milk-sales__error" role="alert">
          {refused}
        </p>
      ) : null}
    </>
  );
}

function ContractRow({ contract, canCheck }: { contract: Contract; canCheck: boolean }) {
  const agreed =
    contract.loads_agreed !== null
      ? `${contract.loads_agreed} × ${contract.litres_per_load !== null ? formatLitres(contract.litres_per_load) : "?"}`
      : "Not stated";
  return (
    <tr>
      <th scope="row">
        {contract.contract_number}
        <p className="milk-sales__detail">{contract.buyer}</p>
        <p className="milk-sales__detail">
          {contract.created_by ? `Recorded by ${contract.created_by}` : "Recorded by a script"}
        </p>
      </th>
      <td className="tabular">
        {formatShortDate(contract.period_from)} to {formatShortDate(contract.period_to)}
      </td>
      <td className="tabular">{agreed}</td>
      <td>
        {formatPrice(contract.price_gbp_per_litre)}, {contract.price_basis}
        <p className="milk-sales__detail">Payment: {contract.payment_terms}</p>
      </td>
      <td className="tabular">
        {contract.loads_recorded} {contract.loads_recorded === 1 ? "load" : "loads"},{" "}
        {formatLitres(contract.litres_recorded)}
      </td>
      <td>
        <Checked canCheck={canCheck} contract={contract} />
      </td>
    </tr>
  );
}

type ContractField =
  | "buyer"
  | "contract_number"
  | "confirmed_on"
  | "period_from"
  | "period_to"
  | "loads_agreed"
  | "litres_per_load"
  | "price"
  | "price_basis"
  | "payment_terms";

export function NewContractForm() {
  const buyers = useBuyers();
  const record = useRecordContract();
  const [buyer, setBuyer] = useState<number | null>(null);
  const [number, setNumber] = useState("");
  const [confirmedOn, setConfirmedOn] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [loads, setLoads] = useState("");
  const [perLoad, setPerLoad] = useState("");
  const [price, setPrice] = useState("");
  const [basis, setBasis] = useState("");
  const [terms, setTerms] = useState("");
  const [documentRef, setDocumentRef] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Partial<Record<ContractField, string>>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);

  const only = buyers.data?.buyers.length === 1 ? buyers.data.buyers[0]!.buyer_id : null;
  const chosen = buyer ?? only;

  const whole = (text: string) => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (record.isPending) return;
    setSummary(null);
    setRecorded(null);
    setRefused(null);
    const found: Partial<Record<ContractField, string>> = {};
    if (chosen === null) found.buyer = "Choose the buyer";
    if (!number.trim()) found.contract_number = "The buyer's contract number is needed";
    if (!isCalendarDate(confirmedOn)) found.confirmed_on = "The day the buyer confirmed it is needed";
    if (!isCalendarDate(from)) found.period_from = "From is needed";
    if (!isCalendarDate(until)) found.period_to = "Until is needed";
    else if (isCalendarDate(from) && until < from) found.period_to = "Until must not be before from";
    if (loads.trim() && !(whole(loads)! > 0)) found.loads_agreed = "A whole number of loads, or leave it empty";
    if (perLoad.trim() && !(whole(perLoad)! > 0)) found.litres_per_load = "Whole litres, or leave it empty";
    const perLitre = parsePrice(price);
    if (perLitre === null || !(perLitre > 0 && perLitre < 2)) {
      found.price = "A price in pence per litre with two decimals at most, such as 41.00";
    }
    if (!basis.trim()) found.price_basis = "Say where the price applies, as the contract says it";
    if (!terms.trim()) found.payment_terms = "The payment terms are needed";
    if (Object.keys(found).length > 0) {
      setErrors(found);
      const n = Object.keys(found).length;
      setSummary(`The contract was not recorded: ${n} ${n === 1 ? "field needs" : "fields need"} attention.`);
      focusFirstInvalid(form.current);
      return;
    }
    setErrors({});
    record.mutate(
      {
        buyer_id: chosen!,
        contract_number: number.trim(),
        confirmed_on: confirmedOn,
        period_from: from,
        period_to: until,
        loads_agreed: whole(loads),
        litres_per_load: whole(perLoad),
        price_gbp_per_litre: perLitre!,
        price_basis: basis.trim(),
        payment_terms: terms.trim(),
        document_ref: documentRef.trim() || null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: (c) => {
          setRecorded(`Recorded: contract ${c.contract_number}`);
          for (const reset of [setNumber, setConfirmedOn, setFrom, setUntil, setLoads, setPerLoad, setPrice,
            setBasis, setTerms, setDocumentRef, setNotes]) {
            reset("");
          }
        },
        onError: (error) => {
          const field = error instanceof PlannerApiError ? error.field : null;
          if (field === "contract_number") {
            setErrors({ contract_number: detailOf(error) });
            focusFirstInvalid(form.current);
          } else {
            setRefused(detailOf(error));
          }
        },
      },
    );
  };

  const text = (
    field: ContractField,
    label: string,
    value: string,
    set: (v: string) => void,
    extra: Partial<Parameters<typeof TextField>[0]> = {},
  ) => (
    <TextField
      errorMessage={errors[field]}
      isInvalid={Boolean(errors[field])}
      label={label}
      onChange={set}
      value={value}
      {...(extra as object)}
    />
  );

  return (
    <section aria-labelledby="new-contract-heading" className="panel">
      <h3 id="new-contract-heading">Add contract</h3>
      <p className="milk-sales__detail">As the buyer's purchase confirmation states it.</p>
      <form className="milk-sales-form" noValidate onSubmit={submit} ref={form}>
        {summary ? (
          <p className="milk-sales__error" role="alert">
            {summary}
          </p>
        ) : null}
        {buyers.isError ? (
          <p className="milk-sales__error">The buyers could not be read: {detailOf(buyers.error)}</p>
        ) : (
          <Select
            errorMessage={errors.buyer}
            isInvalid={Boolean(errors.buyer)}
            label="Buyer"
            onSelectionChange={(key) => setBuyer(key === null ? null : Number(key))}
            placeholder={buyers.isPending ? "Reading the buyers" : "Choose the buyer"}
            selectedKey={chosen === null ? null : String(chosen)}
          >
            {(buyers.data?.buyers ?? []).map((b) => (
              <SelectItem id={String(b.buyer_id)} key={b.buyer_id} textValue={b.name}>
                {b.name}
              </SelectItem>
            ))}
          </Select>
        )}
        <div className="milk-sales-form__pair">
          {text("contract_number", "Contract number", number, setNumber)}
          {text("confirmed_on", "Confirmed on", confirmedOn, setConfirmedOn, { type: "date" })}
        </div>
        <div className="milk-sales-form__pair">
          {text("period_from", "From", from, setFrom, { type: "date" })}
          {text("period_to", "Until", until, setUntil, { type: "date" })}
        </div>
        <div className="milk-sales-form__pair">
          {text("loads_agreed", "Loads agreed", loads, setLoads, { inputMode: "numeric" })}
          {text("litres_per_load", "Litres per load", perLoad, setPerLoad, { inputMode: "numeric" })}
        </div>
        {text("price", "Price (ppl)", price, setPrice, {
          description: "In pence per litre, such as 41.00.",
          inputMode: "decimal",
        })}
        {text("price_basis", "Price basis", basis, setBasis, {
          description: "Where and how the price applies, as written, such as “collected ex Gresford”.",
        })}
        {text("payment_terms", "Payment terms", terms, setTerms, { description: "As agreed, such as “14 days”." })}
        <TextField
          description="Where the signed confirmation is: the mail or the SharePoint file."
          label="Document reference"
          onChange={setDocumentRef}
          value={documentRef}
        />
        <TextArea label="Notes" onChange={setNotes} rows={2} value={notes} />
        <div className="milk-sales__actions">
          <Button isPending={record.isPending} pendingLabel="Recording" type="submit">
            Record contract
          </Button>
        </div>
        <div aria-live="polite">
          {recorded ? (
            <p className="milk-sales__status" role="status">
              {recorded}
            </p>
          ) : null}
        </div>
        {refused ? (
          <p className="milk-sales__error" role="alert">
            {refused}
          </p>
        ) : null}
      </form>
    </section>
  );
}

export function Contracts({ buyer = null }: { buyer?: string | null }) {
  const contracts = useContracts();
  if (contracts.isPending) return <Pending>Reading the contracts</Pending>;
  if (contracts.isError) return <ScreenRefused error={contracts.error} part="contracts" />;
  const rows = contracts.data.contracts.filter((c) => buyer === null || c.buyer === buyer);
  if (rows.length === 0) return <p className="empty">No sales contract is recorded yet.</p>;
  return (
    <Table caption="Sales contracts, the newest period first" className="milk-sales-table">
        <thead>
          <tr>
            <th scope="col">Contract</th>
            <th scope="col">Period</th>
            <th scope="col">Agreed</th>
            <th scope="col">Price</th>
            <th scope="col">Recorded on it</th>
            <th scope="col">Checked</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <ContractRow canCheck={contracts.data.can_check} contract={c} key={c.contract_id} />
          ))}
        </tbody>
      </Table>
  );
}

/** To do: the tickets that wait for a person, the loads that wait for their contract or litres (30 October 2026),
 *  and the tickets rejected lately (16 October 2026). */
export function ToDoParts() {
  const [done, setDone] = useState<string | null>(null);
  const [linked, setLinked] = useState<string | null>(null);
  const heldHeading = useRef<HTMLHeadingElement | null>(null);
  const waitingHeading = useRef<HTMLHeadingElement | null>(null);

  // After a decision the card leaves the list, taking the focused button with it. So the result is
  // said in the status region and focus goes to the list's heading.
  const onDone = (message: string) => {
    setDone(message);
    window.setTimeout(() => heldHeading.current?.focus(), 0);
  };

  return (
    <>
      <section aria-labelledby="held-heading" className="panel">
        <h3 id="held-heading" ref={heldHeading as RefObject<HTMLHeadingElement>} tabIndex={-1}>
          Waiting for the milk office
        </h3>
        <div aria-live="polite">
          {done ? (
            <p className="milk-sales__status" role="status">
              {done}
            </p>
          ) : null}
        </div>
        <HeldLoads onDone={onDone} />
      </section>
      <section aria-labelledby="waiting-heading" className="panel">
        <h3 id="waiting-heading" ref={waitingHeading as RefObject<HTMLHeadingElement>} tabIndex={-1}>
          Loads waiting for their contract
        </h3>
        <div aria-live="polite">
          {linked ? (
            <p className="milk-sales__status" role="status">
              {linked}
            </p>
          ) : null}
        </div>
        <WaitingLoads
          onDone={(message) => {
            setLinked(message);
            window.setTimeout(() => waitingHeading.current?.focus(), 0);
          }}
        />
      </section>
      <section aria-labelledby="rejected-heading" className="panel">
        <h3 id="rejected-heading">Rejected lately</h3>
        <RejectedLately />
      </section>
    </>
  );
}
